import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { masterDataApi } from '@/api/master-data'
import { salesApi } from '@/api/sales'
import { inventoryApi } from '@/api/inventory'
import { dateOnly, dateTimeText } from '@/utils/format'
import type {
  Employee, InventoryDocument, InventoryLog, Product, Ranking, SalesEntry, Stock, Store, UnsettledOrder
} from '@/types/domain'

const documentStatusText = (status: string) => ({
  effective: '已完成', confirmed: '已确认', pending_confirmation: '待确认',
  rejected: '已拒绝', voided: '已作废'
}[status] ?? status ?? '未知')

export const useAppDataStore = defineStore('app-data', () => {
  const products = ref<Product[]>([])
  const stores = ref<Store[]>([])
  const employees = ref<Employee[]>([])
  const sales = ref<SalesEntry[]>([])
  const unsettledOrders = ref<UnsettledOrder[]>([])
  const stocks = ref<Stock[]>([])
  const documents = ref<InventoryDocument[]>([])
  const ledger = ref<InventoryLog[]>([])
  const loading = ref(false)
  const loaded = ref(false)

  const rankings = computed<Ranking[]>(() => {
    const values = new Map<number, Ranking>()
    for (const entry of sales.value) {
      for (const item of entry.items) {
        const product = products.value.find(row => row.id === item.productId)
        if (!product) continue
        const current = values.get(product.id) ?? {
          productId: product.id, name: product.name, unit: product.unit, qty: 0, revenue: 0, profit: 0
        }
        current.qty += item.qty
        current.revenue += item.qty * product.price
        current.profit += item.qty * (product.price - product.cost)
        values.set(product.id, current)
      }
    }
    return [...values.values()].sort((a, b) => b.qty - a.qty)
  })

  const activeStores = computed(() => stores.value.filter(store => store.status === 'active'))
  const activeProducts = computed(() => products.value.filter(product => product.status === 'active'))

  async function load(silent = false) {
    if (!silent) loading.value = true
    try {
      const [productResult, storeResult, employeeResult, salesResult, unsettledResult, stockResult, documentResult, ledgerResult] = await Promise.all([
        masterDataApi.products(), masterDataApi.stores(), masterDataApi.employees(), salesApi.list(), salesApi.unsettled(),
        inventoryApi.stocks(), inventoryApi.documents(), inventoryApi.ledger()
      ])
      products.value = productResult.data.items.map(item => ({
        id: Number(item.id), productCode: item.productCode, name: item.name, unit: item.unit,
        quantityScale: Number(item.quantityScale), price: Number(item.salePrice), cost: Number(item.costPrice),
        status: item.status, used: Boolean(item.used)
      }))
      stores.value = storeResult.data.items.map(item => ({
        id: Number(item.id), storeCode: item.storeCode, name: item.name, address: item.address,
        phone: item.phone, status: item.status, used: Boolean(item.used)
      }))
      employees.value = employeeResult.data.items.map(item => ({
        id: Number(item.id), number: item.employeeNo, name: item.name, phone: item.phone,
        loginAccount: item.loginAccount, hasLoginPassword: Boolean(item.hasLoginPassword), department: item.department,
        joinDate: dateOnly(item.joinDate), status: item.status,
        storeIds: (item.stores ?? []).map(store => Number(store.id))
      }))
      sales.value = salesResult.map(item => ({
        id: Number(item.id), no: item.submissionNo, date: dateOnly(item.businessDate), period: item.period,
        storeId: Number(item.storeId), store: item.storeName, submitterName: item.submitterName,
        submittedAt: dateTimeText(item.submittedAt), orders: Number(item.orderCount), revenue: Number(item.revenue),
        cost: Number(item.cost), received: Number(item.received), unsettled: Number(item.unsettled),
        wechat: Number(item.wechat), alipay: Number(item.alipay), cash: Number(item.cash),
        items: (item.items ?? []).map(row => ({ productId: Number(row.productId), qty: Number(row.quantity) }))
      }))
      unsettledOrders.value = unsettledResult.map(item => ({
        id: String(item.id), orderNo: item.orderNo, date: dateOnly(item.businessDate), storeId: Number(item.storeId),
        store: item.storeName, period: item.period, time: dateTimeText(item.createdAt).slice(-8, -3),
        amount: Number(item.amount), remark: item.remark, submitterName: item.submitterName
      }))
      stocks.value = stockResult.map(item => ({
        id: Number(item.id), storeId: Number(item.storeId), productId: Number(item.productId), qty: Number(item.quantity),
        safety: Number(item.safetyStock), version: Number(item.version), status: item.status
      }))
      documents.value = documentResult.map(item => ({
        id: Number(item.id), type: item.type, no: item.documentNo, date: dateOnly(item.businessDate),
        storeId: Number(item.storeId), toStoreId: item.toStoreId ? Number(item.toStoreId) : null,
        reason: item.reasonText ?? '', remark: item.reasonText ?? '', status: documentStatusText(item.status),
        voidReason: item.voidReason, producerName: item.createdByName, submittedAt: dateTimeText(item.createdAt),
        items: (item.items ?? []).map(row => ({ productId: Number(row.productId), qty: Number(row.quantity), remark: row.remark }))
      }))
      ledger.value = ledgerResult.map(item => ({
        id: Number(item.id), time: dateTimeText(item.time), storeId: Number(item.storeId), productId: Number(item.productId),
        type: item.type, delta: Number(item.delta), before: Number(item.before), after: Number(item.after),
        operator: item.operator, remark: item.remark, source: item.source
      }))
      loaded.value = true
    } finally {
      loading.value = false
    }
  }

  const productById = (id: number) => products.value.find(item => item.id === Number(id))
  const storeById = (id: number) => stores.value.find(item => item.id === Number(id))

  return {
    products, stores, employees, sales, unsettledOrders, stocks, documents, ledger, loading, loaded,
    rankings, activeStores, activeProducts, load, productById, storeById
  }
})
